import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { QueryClient } from "@tanstack/react-query";
import axios from "axios";
import type { NavigateFunction } from "react-router-dom";
import strings from "../global/strings";
import type { BilledItem } from "../components/invoice-template/invoice-template.component";
import type { Company, CompanySnapshotData } from "./company.queries";
import type { Customer } from "./customer.queries";

// Mirrors billing_system_BE/models/creditNote.js, as nested onto a voided
// Bill via the `hasOne(CreditNote)` association (bill.controller.js's
// getBillBy/getCompanyBill/getCompanyBillById all include it the same way).
export interface CreditNote {
	credit_note_id: string;
	bill_id: string;
	buyer_pan?: string | null;
	buyer_name: string;
	fiscal_year: string;
	ref_invoice_number: string;
	credit_note_number: string;
	credit_note_date_nepali: string;
	credit_note_date: string;
	reason_for_return: string;
	sub_total: number;
	total_sales: number;
	print_copies: number;
	billed_items: BilledItem[];
	company_user?: { user?: { user_id?: number; first_name?: string; last_name?: string } };
}

// Mirrors billing_system_BE/models/bill.js in full, plus the associations
// getBillBy/getCompanyBill/getCompanyBillById all include (bill.controller.js):
// Company, CompanySnapshot (as company_snapshot), CompanyUsers->User,
// CreditNote, CompanyCustomers (as customer_info).
export interface Bill {
	bill_id: string;
	company_info_id: string;
	company_user_id: string;
	company_customer_id?: string | null;
	record_id?: string | null;
	record_type?: string | null;
	// Present whenever this bill is linked to a connected external system
	// (Model A: pushed in via record_id; Model B: customer has an
	// external_customer_id) — see bill.controller.js's createBillMetaIfNeeded.
	bill_meta?: {
		bill_meta_id: string;
		system_type: string;
		ext_synced: string;
		ext_synced_at?: string | null;
	} | null;
	buyer_pan?: string | null;
	buyer_name: string;
	customer_mobile?: string | null;
	bill_for: string;
	fiscal_year: string;
	invoice_number: string;
	invoice_date_nepali: string;
	invoice_date: string;
	invoice_month: number;
	sales_date: string;
	sub_total: number;
	total_sales: number;
	payment_mode: string;
	non_taxable_amt: number;
	taxable_sales_vat: number;
	non_taxable_discount: number;
	taxable_discount: number;
	non_taxable_service_charge: number;
	taxable_service_charge: number;
	vat: number;
	excisable_amount: number;
	excise: number;
	taxable_sales_hst: number;
	hst: number;
	amount_for_esf: number;
	esf: number;
	export_sales: number;
	tax_exempted_sales: number;
	sale_info?: string | null;
	isrealtime: boolean;
	datetimeClient: string;
	print_copies: number;
	bill_void: boolean;
	bill_canceled: boolean;
	ird_bill: "pending" | "success" | "failed";
	ird_synced_at?: string | null;
	lastPrintDate?: string | null;
	billed_items: BilledItem[];
	company?: Partial<Company>;
	// company_snapshots row this bill was created under — { company, preference,
	// identification } as of the moment the bill was created. InvoiceTemplate
	// reads this exclusively (via a hasSnapshot ternary, never `??`) whenever
	// it's present, falling back to the live `company` association only for
	// pre-snapshot bills and drafts — see invoice-template.component.tsx.
	company_snapshot?: { company_snapshot_id: string; version_number: number; data: CompanySnapshotData } | null;
	company_user?: {
		user?: { user_id?: number; first_name?: string; last_name?: string; user_email?: string; user_mobile?: string };
	};
	customer_info?: Omit<Customer, "createdAt" | "updatedAt"> | null;
	credit_note?: CreditNote | null;
	createdAt?: string;
	updatedAt?: string;
}

// Shared by BillSummaryCard's callers (invoice-bill.page.tsx, direct-bill,
// bill-bulk-print) to build a "Recorded By" display name from a Bill/
// CreditNote's populated company_user association. Draft's company_user
// isn't populated by the API, so this is never called for drafts.
export function recordedByName(
	companyUser?: { user?: { first_name?: string; last_name?: string } } | null,
): string | undefined {
	const name = [companyUser?.user?.first_name, companyUser?.user?.last_name].filter(Boolean).join(" ");
	return name || undefined;
}

// Matches bill-filter.component.tsx's filterFormik values, transformed the
// same way invoice-bill.page.tsx's onSubmit already does before dispatch
// (bill_void parsed to boolean-as-string, date_from/to dropped when
// fiscal_year is set, customer_id prefixed "CUS_", bill_for joined to a
// "#tag1#tag2" string) — this is the exact POST body bill/search
// (billing_system_BE controllers/bill.controller.js:getBillBy) expects.
// Friendly, UI-facing sort presets — never sent to the API as-is. BILL_SORT_MAP
// below translates each to the generic {sort_by, sort_order} pair bill/search
// (billing_system_BE controllers/bill.controller.js's getBillBy) actually
// accepts, so the API stays agnostic of these labels and can be resorted/
// relabeled here without touching the BE contract.
export type BillSortOrder =
	| "invoice_number_desc"
	| "invoice_number_asc"
	| "invoice_date_desc"
	| "invoice_date_asc"
	| "sales_date_desc"
	| "sales_date_asc"
	| "total_sales_desc"
	| "total_sales_asc"
	| "recent_added"
	| "recent_updated"
	| "last_print_date_desc"
	| "last_print_date_asc";

const DEFAULT_BILL_SORT: BillSortOrder = "invoice_number_desc";

const BILL_SORT_MAP: Record<BillSortOrder, { sort_by: string; sort_order: "asc" | "desc" }> = {
	invoice_number_desc: { sort_by: "invoice_number", sort_order: "desc" },
	invoice_number_asc: { sort_by: "invoice_number", sort_order: "asc" },
	invoice_date_desc: { sort_by: "invoice_date", sort_order: "desc" },
	invoice_date_asc: { sort_by: "invoice_date", sort_order: "asc" },
	sales_date_desc: { sort_by: "sales_date", sort_order: "desc" },
	sales_date_asc: { sort_by: "sales_date", sort_order: "asc" },
	total_sales_desc: { sort_by: "total_sales", sort_order: "desc" },
	total_sales_asc: { sort_by: "total_sales", sort_order: "asc" },
	recent_added: { sort_by: "created_at", sort_order: "desc" },
	recent_updated: { sort_by: "updated_at", sort_order: "desc" },
	last_print_date_desc: { sort_by: "last_print_date", sort_order: "desc" },
	last_print_date_asc: { sort_by: "last_print_date", sort_order: "asc" },
};

export interface BillSearchFilters {
	customer_id?: string;
	buyer_name?: string;
	invoice_number?: string;
	buyer_pan?: string;
	date_from?: string;
	date_to?: string;
	total_sales_min?: number;
	total_sales_max?: number;
	fiscal_year?: string;
	bill_void?: boolean | "";
	bill_for?: string;
	company_user_id?: string;
	ird_bill?: boolean | "";
	bill_canceled?: boolean | "";
	sort?: BillSortOrder;
}

// getBillBy's two count sources are NOT the same scope: totalBill/maxSales/
// totalCreditNote/totalFailed/totalCanceled come from a raw SQL query scoped
// only to company_info_id (i.e. company-wide, ignoring every other filter) —
// that's intentional existing behavior (the header stat boxes show overall
// company totals, not "matches for this search"). filteredTotal/totalPages/
// bill are the only fields actually scoped to the submitted filters.
export interface BillSearchResult {
	bill: Bill[];
	totalBill: number;
	maxSales: number;
	totalCreditNote: number;
	totalFailed: number;
	totalCanceled: number;
	filteredTotal: number;
	totalPages: number;
	currentPage: number;
	limit: number;
}

const INFINITE_LIST_PAGE_SIZE = 30;
// bill/search hard-caps limit at 100 server-side (getBillBy) — the ceiling
// for "give me everything matching this filter in one shot" reads used by
// BillDuplicate/BillBulkPrint's selection lists, not a number chosen here.
const SELECTION_PAGE_SIZE = 100;

function billsQueryKey(filters: BillSearchFilters) {
	return ["bills", "search", filters] as const;
}

async function fetchBillsPage(
	filters: BillSearchFilters,
	page: number,
	limit: number,
): Promise<BillSearchResult> {
	const { sort, ...rest } = filters;
	const { sort_by, sort_order } = BILL_SORT_MAP[sort ?? DEFAULT_BILL_SORT];
	const res = await axios.post(
		`${strings.url}bill/search`,
		{ ...rest, sort_by, sort_order, page, limit },
		{ withCredentials: true },
	);

	if (!res.data.success) {
		throw new Error(res.data.message);
	}

	return res.data.result;
}

// Drives invoice-bill.page.tsx's main list — infinite/paginated, 30 bills per
// page, loaded as the user scrolls (see useInfiniteScrollSentinel). Deliberately
// a SEPARATE cache entry from useBillsForSelection even when called with
// identical filters — the two have incompatible pagination semantics (partial,
// scroll-driven vs. one-shot "everything up to the server cap") and must not
// be conflated into one shared query.
export function useInfiniteBills(filters: BillSearchFilters, options?: { enabled?: boolean }) {
	return useInfiniteQuery({
		queryKey: [...billsQueryKey(filters), "infinite"] as const,
		queryFn: ({ pageParam }) => fetchBillsPage(filters, pageParam, INFINITE_LIST_PAGE_SIZE),
		initialPageParam: 1,
		getNextPageParam: (lastPage) =>
			lastPage.currentPage < lastPage.totalPages ? lastPage.currentPage + 1 : undefined,
		enabled: options?.enabled,
	});
}

// Full (capped at 100) non-paginated read for "pick bills matching this
// filter" flows — BillDuplicate and BillBulkPrint, both opened as modals off
// bill-filter.component.tsx with the SAME filters the main list is showing,
// but needing every match (not just whatever's scrolled into view) to select
// from. If a filtered set exceeds 100 bills, only the first 100 are offered —
// an existing server-side ceiling (getBillBy), not introduced here.
export function useBillsForSelection(filters: BillSearchFilters, options?: { enabled?: boolean }) {
	return useQuery({
		queryKey: [...billsQueryKey(filters), "selection"] as const,
		queryFn: () => fetchBillsPage(filters, 1, SELECTION_PAGE_SIZE),
		enabled: options?.enabled ?? true,
	});
}

// One-shot, uncapped-pagination read backing error-fix.page.tsx's "unsynced
// bills" queue — same bill/search endpoint as useBillsForSelection, just a
// fixed filter (unsynced, non-canceled) instead of the caller's own filter
// bag, so it gets its own cache key rather than sharing billsQueryKey.
export const failedBillsQueryKey = ["bills", "failed"] as const;

async function fetchFailedBills(): Promise<Bill[]> {
	const result = await fetchBillsPage({ bill_canceled: false, ird_bill: false }, 1, SELECTION_PAGE_SIZE);
	return result.bill;
}

export function useFailedBills() {
	return useQuery({
		queryKey: failedBillsQueryKey,
		queryFn: fetchFailedBills,
	});
}

// Splits the flat failed-bill list into "created by me" vs "created by
// teammates" — error-fix.page.tsx's My Bills/Team Bills tabs. Mirrors
// deriveDraftLists() in drafts.queries.ts (same shape of problem: one shared
// query, page derives its own groupings from it).
export function deriveFailedBillLists(
	bills: Bill[] | undefined,
	currentUserId?: string,
): { user: Bill[]; all: Bill[] } {
	const grouped: { user: Bill[]; all: Bill[] } = { user: [], all: [] };
	if (!bills) return grouped;

	for (const bill of bills) {
		if (bill.company_user_id === currentUserId) {
			grouped.user.push(bill);
		} else {
			grouped.all.push(bill);
		}
	}
	return grouped;
}

export interface RetryIRDBillVars {
	bill_id: string;
}

// bill/retry/ird's success `result` is the bill row with ird_bill flipped to
// "success" (billing_system_BE controllers/bill.controller.js:retryIRDBill) —
// on failure (duplicate/offline/etc.) the caller branches on `code`, same
// contract as the old retryIRDBill thunk, so this throws a typed error
// carrying both fields rather than just a message.
export class RetryIRDBillError extends Error {
	code?: number;
	result?: Partial<Bill>;
	constructor(message: string, code?: number, result?: Partial<Bill>) {
		super(message);
		this.code = code;
		this.result = result;
	}
}

async function postRetryIRDBill(vars: RetryIRDBillVars): Promise<Bill> {
	const res = await axios.post(`${strings.url}bill/retry/ird`, vars, {
		withCredentials: true,
	});

	if (!res.data.success) {
		throw new RetryIRDBillError(res.data.message, res.data.code, res.data.result);
	}

	return res.data.result;
}

// Patches ird_bill: "success" onto the matching bill across every cached
// infinite bill-list query (there can be more than one live filter combo
// cached at once) instead of invalidating — a full refetch would re-fetch
// every page the user has already scrolled through just to flip one flag,
// and would fight the user's current scroll position.
function patchBillIrdStatus(queryClient: QueryClient, billId: string) {
	queryClient.setQueriesData<{ pages: BillSearchResult[]; pageParams: unknown[] } | undefined>(
		{ queryKey: ["bills", "search"], predicate: (query) => query.queryKey.includes("infinite") },
		(data) => {
			if (!data) return data;
			return {
				...data,
				pages: data.pages.map((page) => ({
					...page,
					bill: page.bill.map((bill) =>
						bill.bill_id === billId ? { ...bill, ird_bill: "success" as const } : bill,
					),
				})),
			};
		},
	);
}

// Shared by credit-note-form.component.tsx's failure-recovery retry and
// error-fix.page.tsx's "Sync IRD" button — the same bill/retry/ird call, two
// different callers with their own success/failure UI.
export function useRetryIRDBill() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: postRetryIRDBill,
		onSuccess: (bill) => {
			patchBillIrdStatus(queryClient, bill.bill_id);
			// Can't just filter the synced bill's own id out of the cache here:
			// on a "duplicate detected" retry (code 91), the id that actually
			// gets retried/synced (error.result.bill_id, the suggested
			// replacement) is a different bill than the one the user originally
			// selected — the server cancels that original bill as part of the
			// same cascade (see error-fix.page.tsx's handleRetrySync), so it
			// needs to drop out of the unsynced queue too even though its id
			// never appears in this mutation's result. A refetch reflects
			// whatever the server actually did instead of guessing which ids
			// changed. No-op if these queries were never mounted (credit-note-
			// form's retry path doesn't touch either).
			queryClient.invalidateQueries({ queryKey: failedBillsQueryKey });
			queryClient.invalidateQueries({ queryKey: billDatesQueryKey });
		},
		onError: (error) => writeIrdDownOnServerOffline(queryClient, error),
	});
}

// The old Redux thunks (addBill/addDraftBill/retryIRDBill) each threw this
// exact code-92 write inline via the shared queryClient singleton (see
// bill.action.js) whenever IRD reported itself offline. Centralized so every
// Query-based mutation that can fail with code 92 stays in sync with that.
function writeIrdDownOnServerOffline(queryClient: QueryClient, error: unknown) {
	const code = error instanceof RetryIRDBillError ? error.code : error instanceof BillSubmitError ? error.code : undefined;
	if (code === 92) {
		queryClient.setQueryData(irdStatusQueryKey, false);
		localStorage.setItem("irdStatus", JSON.stringify(false));
	}
}

// bill/add and bill/draft/add fail with the same server `code` contract
// (91: bills need fixing first, 92: IRD offline, 93: verification failed,
// 94: navigate to /error, default: unknown) — one error type and one
// message/navigate handler for both, replacing the switch each old thunk
// (addBill/addDraftBill in bill.action.js) duplicated inline.
export class BillSubmitError extends Error {
	code?: number;
	result?: unknown;
	constructor(message: string, code?: number, result?: unknown) {
		super(message);
		this.code = code;
		this.result = result;
	}
}

// Loose bag, not a typed InvoiceFormValues[] — the real shape lives with
// invoice-form/customer-bulk-draft's formik values, which already diverge
// from each other in ad hoc ways (same reasoning as useFilteredCustomers'
// filter bag in customer.queries.ts). This layer only cares that it's an
// array of bill payloads to forward as-is.
export interface AddBillVars {
	bills: Record<string, unknown>[];
}

async function postAddBill(vars: AddBillVars): Promise<Bill> {
	const res = await axios.post(`${strings.url}bill/add`, vars, { withCredentials: true });

	if (!res.data.success) {
		throw new BillSubmitError(res.data.message, res.data.code, res.data.result);
	}

	return res.data.result;
}

export function useAddBill() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: postAddBill,
		onError: (error) => writeIrdDownOnServerOffline(queryClient, error),
	});
}

export interface AddDraftBillVars {
	bills: Record<string, unknown>[];
	username?: string;
	password?: string;
	seller_pan?: string;
	user_role?: string;
}

async function postAddDraftBill(vars: AddDraftBillVars): Promise<unknown> {
	const res = await axios.post(`${strings.url}bill/draft/add`, vars, { withCredentials: true });

	if (!res.data.success) {
		throw new BillSubmitError(res.data.message, res.data.code, res.data.result);
	}

	return res.data.result;
}

export function useAddDraftBill() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: postAddDraftBill,
		onError: (error) => writeIrdDownOnServerOffline(queryClient, error),
	});
}

// Same navigate contract the old addBill/addDraftBill thunks each inlined
// per call site (direct-bill.component.tsx, ready-draft-list's two call
// sites) — centralized so it's not tripled across callers. No toast: /error
// (internal-error.page.jsx's ERROR_MAP) already renders a persistent,
// code-specific explanation on arrival, so a toast fired the instant before
// this navigation would only be saying the same thing, worse (a few seconds,
// competing with the page transition, easy to miss).
export function handleBillSubmitError(error: unknown, navigate: NavigateFunction) {
	const code = error instanceof BillSubmitError ? error.code : undefined;
	navigate("/error", { state: { code: code ?? "default" } });
}

// creditNote/add (billing_system_BE/controllers/bill.controller.js:2537)
// returns the same {success, code, message} contract as bill/add and
// bill/draft/add (91: bills need fixing first, 92: IRD offline, 93:
// verification failed, 94: navigate to /error, default: unknown) — reuses
// BillSubmitError/writeIrdDownOnServerOffline rather than a second switch.
// Code 95 ("bill isn't synced with IRD yet") is unique to credit notes and
// deliberately NOT routed through handleBillSubmitError by callers — it's a
// recoverable retry-then-resubmit case the component itself handles.
export type AddCreditNoteVars = Partial<Bill> & {
	ref_invoice_number: string;
	credit_note_date: string;
	reason_for_return: string;
};

async function postAddCreditNote(vars: AddCreditNoteVars): Promise<void> {
	const res = await axios.post(`${strings.url}creditNote/add`, vars, {
		withCredentials: true,
	});

	if (!res.data.success) {
		throw new BillSubmitError(res.data.message, res.data.code, res.data.result);
	}
}

export function useAddCreditNote() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: postAddCreditNote,
		onSuccess: () => {
			// The referenced bill is voided and a new credit note row appears —
			// invalidate every active bills/search cache entry (prefix match)
			// rather than one specific filter set, same as other bill mutations.
			queryClient.invalidateQueries({ queryKey: ["bills"] });
		},
		onError: (error) => writeIrdDownOnServerOffline(queryClient, error),
	});
}

export interface IncrementPrintCountVars {
	updates: Array<{ id: string; model_type: "bill" | "credit"; print_type: "print" | "download" }>;
}

// No cache side-effect needed on success: print_copies isn't rendered
// anywhere in the bill list rows (bill-list-item.component.tsx), only in the
// detail panel, which already applies its own optimistic local update
// (invoice-bill.page.tsx's updatePrintCount) right after this resolves.
async function putIncrementPrintCount(vars: IncrementPrintCountVars): Promise<void> {
	const res = await axios.put(`${strings.url}bill/update/print`, vars.updates, {
		withCredentials: true,
	});

	if (!res.data.success) {
		throw new Error(res.data.message);
	}
}

export function useIncrementPrintCount() {
	return useMutation({
		mutationFn: putIncrementPrintCount,
	});
}

// Backs top-navigation's unsynced-bill badge, invoice-bill.page's "fix
// unsynced bills" Swal gate, create-invoice/direct-bill's "Last Bill Date"
// display, and company-setting's earliest-draft-date preference-change gate —
// all four previously duplicate-fetched via their own `getBillDates()` Redux
// dispatch on mount. One shared queryKey means TanStack dedupes the fetch
// across whichever of them are mounted at once (mirrors getLatestBillDate in
// billing_system_BE/controllers/bill.controller.js:1609).
export interface BillDatesResult {
	lastInvoiceDate: string;
	firstDraftDate: string;
	failedBills: number;
}

export const billDatesQueryKey = ["bill", "dates"] as const;

async function fetchBillDates(): Promise<BillDatesResult> {
	const res = await axios.get(`${strings.url}bill/last/date`, {
		withCredentials: true,
	});

	if (!res.data.success) {
		throw new Error(res.data.message);
	}

	return res.data.result;
}

export function useBillDates() {
	return useQuery({
		queryKey: billDatesQueryKey,
		queryFn: fetchBillDates,
	});
}

// Shared query-cache key for the IRD-server-reachability flag, replacing
// billServerStatusReducer. Written from useIrdStatus()'s own check mutation
// and from writeIrdDownOnServerOffline() (this file's addBill/addDraftBill/
// retryIRDBill/addCreditNote mutations, IRD-server-down error code 92).
export const irdStatusQueryKey = ["irdStatus"] as const;

export async function postCheckIrdStatus(): Promise<{ status: boolean; message: string }> {
	const res = await axios.post(
		`${strings.url}bill/check/ird`,
		{},
		{ withCredentials: true },
	);

	return { status: res.data.status, message: res.data.message };
}

// ---------------------------------------------------------------------------
// BE-rendered PDF printing (bill/credit-note/draft/bulk). Replaces the old
// react-to-print + InvoiceTemplate approach for these four surfaces only —
// audit log and the customer-facing portal (no BE PDF endpoint there yet)
// are unaffected. See print.controller.js (billing_system_BE) for the
// generation side; none of these endpoints touch print_copies, which stays
// owned by useIncrementPrintCount above.
export type PrintSize = "a4" | "a3" | "a5" | "thermal_80" | "thermal_58";

export const PRINT_SIZES: Array<{ id: PrintSize; label: string }> = [
	{ id: "a4", label: "A4" },
	{ id: "a3", label: "A3" },
	{ id: "a5", label: "A5" },
	{ id: "thermal_80", label: "Thermal 80mm" },
	{ id: "thermal_58", label: "Thermal 58mm" },
];

// Safari/Firefox only allow window.open to skip the popup blocker when it's
// called synchronously within the click's own call stack — by the time a
// blob response comes back from an awaited axios call, that gesture window
// has closed, and window.open gets silently blocked (no exception, nothing
// for FormError to show). So we open a blank tab synchronously, before the
// mutationFn's first await, and re-point it at the real PDF once it's ready.
function openPendingPdfTab(): Window | null {
	const tab = window.open("", "_blank");
	tab?.document.write(
		"<title>Generating PDF…</title><body style='font:14px sans-serif;padding:2em;color:#555'>Generating PDF…</body>",
	);
	return tab;
}

// Points the pending tab (or, if it got blocked anyway, opens a fresh one —
// no worse than the old behavior) at the resolved PDF blob, and revokes the
// object URL shortly after — the tab has had time to load it by then, and we
// don't want to leak blob URLs if the user prints many bills in one session.
function openPdfBlob(blob: Blob, pendingTab: Window | null) {
	const url = URL.createObjectURL(blob);
	const tab = pendingTab && !pendingTab.closed ? pendingTab : window.open(url, "_blank");
	if (tab) tab.location.href = url;
	setTimeout(() => URL.revokeObjectURL(url), 10000);
}

// These are useMutation, not useQuery, even though the underlying calls are
// GETs: a query would auto-fetch on mount/param-change, which is exactly
// what we don't want (PDF generation is a real, non-trivial BE render — it
// should only ever run from an explicit Print click's own .mutate() call).
export function usePrintBill() {
	return useMutation({
		mutationFn: async ({ billId, size }: { billId: string; size: PrintSize }) => {
			const pendingTab = openPendingPdfTab();
			try {
				const res = await axios.get(`${strings.url}bill/print`, {
					params: { bill_id: billId, size },
					withCredentials: true,
					responseType: "blob",
				});
				openPdfBlob(res.data, pendingTab);
			} catch (err) {
				pendingTab?.close();
				throw err;
			}
		},
	});
}

export function usePrintCreditNote() {
	return useMutation({
		mutationFn: async ({ creditNoteId, size }: { creditNoteId: string; size: PrintSize }) => {
			const pendingTab = openPendingPdfTab();
			try {
				const res = await axios.get(`${strings.url}bill/print/credit-note`, {
					params: { credit_note_id: creditNoteId, size },
					withCredentials: true,
					responseType: "blob",
				});
				openPdfBlob(res.data, pendingTab);
			} catch (err) {
				pendingTab?.close();
				throw err;
			}
		},
	});
}

export function usePrintDraft() {
	return useMutation({
		mutationFn: async ({ draftId, size }: { draftId: string; size: PrintSize }) => {
			const pendingTab = openPendingPdfTab();
			try {
				const res = await axios.get(`${strings.url}bill/print/draft`, {
					params: { draft_id: draftId, size },
					withCredentials: true,
					responseType: "blob",
				});
				openPdfBlob(res.data, pendingTab);
			} catch (err) {
				pendingTab?.close();
				throw err;
			}
		},
	});
}

export function usePrintBulkBills() {
	return useMutation({
		mutationFn: async ({ billIds, size }: { billIds: string[]; size: PrintSize }) => {
			const pendingTab = openPendingPdfTab();
			try {
				const res = await axios.post(
					`${strings.url}bill/print/bulk`,
					{ bill_ids: billIds, size },
					{ withCredentials: true, responseType: "blob" },
				);
				openPdfBlob(res.data, pendingTab);
			} catch (err) {
				pendingTab?.close();
				throw err;
			}
		},
	});
}

// Matches billing_system_BE controllers/print.controller.js:printSalesBook's
// duration contract — month is always 1-12 for both calendars. "month" mode is
// just a "range" with from === to on the BE, kept as its own discriminant here
// only because the dialog UI treats them as separate tabs.
export type SalesBookCalendar = "en" | "np";

export type SalesBookDuration =
	| { duration_type: "month"; calendar: SalesBookCalendar; year: number; month: number }
	| {
			duration_type: "range";
			calendar: SalesBookCalendar;
			from_year: number;
			from_month: number;
			to_year: number;
			to_month: number;
	  }
	| { duration_type: "fiscal_year"; fiscal_year: string };

export function usePrintSalesBook() {
	return useMutation({
		mutationFn: async ({
			duration,
			useEnDate,
		}: {
			duration: SalesBookDuration;
			useEnDate: boolean;
		}) => {
			const pendingTab = openPendingPdfTab();
			try {
				const res = await axios.post(
					`${strings.url}bill/print/sales-book`,
					{ ...duration, use_en_date: useEnDate },
					{ withCredentials: true, responseType: "blob" },
				);
				openPdfBlob(res.data, pendingTab);
			} catch (err) {
				pendingTab?.close();
				throw err;
			}
		},
	});
}
